import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import ReactMarkdown from "react-markdown";

type Msg = { role: "user" | "assistant"; content: string };

export default function AskAiSheet({
  showTrigger = true,
  open: openProp,
  onOpenChange,
  variant = "ai",
}: {
  /** Hide the floating trigger when another FAB controls this sheet. */
  showTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
  /** "support" renders the marketing-site support assistant copy. */
  variant?: "ai" | "support";
} = {}) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (o: boolean) => {
    setOpenState(o);
    onOpenChange?.(o);
  };
  const isSupport = variant === "support";
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const setUnreadCount = (n: number) => {
    setUnread(n);
    window.dispatchEvent(new CustomEvent("chat:unread", { detail: n }));
  };

  // Clear the badge as soon as the doctor opens the conversation.
  useEffect(() => {
    if (open) setUnreadCount(0);
  }, [open]);


  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "i") { e.preventDefault(); setOpen(!open); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const next = [...messages, { role: "user" as const, content: input }];
    setMessages(next); setInput(""); setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("patient-context-chat", { body: { messages: next } });
      if (error) throw error;
      // Simple non-streaming fallback: read text
      const text = typeof data === "string" ? data : (data as any)?.text || JSON.stringify(data);
      // The function streams SSE; supabase.functions.invoke returns raw. Extract deltas heuristically.
      const parsed = extractDeltas(text) || text;
      setMessages([...next, { role: "assistant", content: parsed }]);
      if (!open) setUnreadCount(unread + 1);
    } catch (e: any) {
      setMessages([...next, { role: "assistant", content: `Error: ${e.message}` }]);
      if (!open) setUnreadCount(unread + 1);
    }
    setBusy(false);
  };

  function extractDeltas(raw: string): string {
    if (!raw.includes("data:")) return "";
    const out: string[] = [];
    for (const line of raw.split("\n")) {
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      try {
        const j = JSON.parse(payload);
        const d = j.choices?.[0]?.delta?.content;
        if (d) out.push(d);
      } catch {}
    }
    return out.join("");
  }

  return (
    <>
      {showTrigger && (
        <button
          onClick={() => setOpen(true)}
          title={isSupport ? "Chat with us" : "Ask AI (⌘I)"}
          className="fixed bottom-6 right-6 z-30 h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 transition"
        >
          {isSupport ? <MessageCircle className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 min-w-5 animate-bounce items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground shadow-md">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="flex flex-col w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {isSupport ? <MessageCircle className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              {isSupport ? "Chat with DoctorsX" : "Ask AI"}
            </SheetTitle>
          </SheetHeader>
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 py-4">
            {messages.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-8">
                {isSupport
                  ? "Hi! Ask us anything about DoctorsX — pricing, features, onboarding or a live demo."
                  : `Ask anything clinical: "First-line for pediatric otitis media?" "Dose of amoxicillin for 20kg child?"`}
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`p-3 rounded-lg text-sm ${m.role === "user" ? "bg-primary/10 ml-8" : "bg-muted mr-8"}`}>
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <ReactMarkdown>{m.content}</ReactMarkdown>
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>}
          </div>
          <div className="flex gap-2 border-t pt-3">
            <Input placeholder="Ask a clinical question…" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
            <Button onClick={send} disabled={busy || !input.trim()}><Send className="h-4 w-4" /></Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
